import type { ShowNotificationPayload } from "design-components";

/** React Router location state for toast that must survive detail → list navigation (AV-53). */
export interface CustomersListLocationState {
  flashNotification?: ShowNotificationPayload;
}

const FLASH_DEDUPE_TTL_MS = 10_000;
const FLASH_SESSION_KEY_PREFIX = "aviro.customers.list.flash:";

function flashKey(flash: ShowNotificationPayload): string {
  return `${flash.variant}\0${flash.title}\0${flash.description}`;
}

function wasFlashConsumed(key: string): boolean {
  try {
    return sessionStorage.getItem(FLASH_SESSION_KEY_PREFIX + key) != null;
  } catch {
    return false;
  }
}

function markFlashConsumed(key: string): void {
  try {
    const storageKey = FLASH_SESSION_KEY_PREFIX + key;
    sessionStorage.setItem(storageKey, "1");
    window.setTimeout(() => {
      try {
        sessionStorage.removeItem(storageKey);
      } catch {
        /* ignore */
      }
    }, FLASH_DEDUPE_TTL_MS);
  } catch {
    /* private mode — Strict Mode may double-toast */
  }
}

export function readCustomersListFlashNotification(
  state: unknown,
): ShowNotificationPayload | null {
  if (state == null || typeof state !== "object") return null;
  if (!("flashNotification" in state)) return null;
  const flash = (state as CustomersListLocationState).flashNotification;
  if (flash == null || typeof flash !== "object") return null;
  if (typeof flash.title !== "string" || typeof flash.description !== "string") return null;
  if (
    flash.variant !== "success" &&
    flash.variant !== "error" &&
    flash.variant !== "warning" &&
    flash.variant !== "info"
  ) {
    return null;
  }
  return {
    variant: flash.variant,
    title: flash.title,
    description: flash.description,
    ...(flash.actions != null ? { actions: flash.actions } : {}),
  };
}

/**
 * Returns the flash once, then null on subsequent reads of the same payload
 * (Strict Mode double-effect / remount). Uses sessionStorage (D-AV53-010).
 */
export function consumeCustomersListFlashNotification(
  state: unknown,
): ShowNotificationPayload | null {
  const flash = readCustomersListFlashNotification(state);
  if (!flash) return null;
  const key = flashKey(flash);
  if (wasFlashConsumed(key)) return null;
  markFlashConsumed(key);
  return flash;
}
